import { SunnyRequestBuilder } from '@/models/Common/SunnyRequestBuilder';
import { ITransportationSearchStationsRequestParams } from '@/models/Transportation/interfaces/ITransportationSearchStationsRequestParams';
import { ITransportationSearchTrainFaresRequestParams } from '@/models/Transportation/interfaces/ITransportationSearchTrainFaresRequestParams';
import { ITransportationSearchTrainPackageFaresRequestParams } from '@/models/Transportation/interfaces/ITransportationSearchTrainPackageFaresRequestParams';
import { ITransportationSearchTrainScheduleRequestParams } from '@/models/Transportation/interfaces/ITransportationSearchTrainScheduleRequestParams';
import { ITransportationSearchTrainSeatMapCarriageRequestParams } from '@/models/Transportation/interfaces/ITransportationSearchTrainSeatMapCarriageRequestParams';
import { ITransportationSearchTrainSeatMapCarriagesRequestParams } from '@/models/Transportation/interfaces/ITransportationSearchTrainSeatMapCarriagesRequestParams';
import { ITransportationSearchTrainsRequestParams } from '@/models/Transportation/interfaces/ITransportationSearchTrainsRequestParams';
import TransportationSearchStationsJSC from '@/schema/TransportationSearchStationsJSC';
import TransportationSearchTrainFaresJSC from '@/schema/TransportationSearchTrainFaresJSC';
import TransportationSearchTrainPackageFaresJSC from '@/schema/TransportationSearchTrainPackageFaresJSC';
import TransportationSearchTrainScheduleJSC from '@/schema/TransportationSearchTrainScheduleJSC';
import TransportationSearchTrainSeatMapCarriageJSC from '@/schema/TransportationSearchTrainSeatMapCarriageJSC';
import TransportationSearchTrainSeatMapCarriagesJSC from '@/schema/TransportationSearchTrainSeatMapCarriagesJSC';
import TransportationSearchTrainsJSC from '@/schema/TransportationSearchTrainsJSC';
import { IRequestWrap } from '@/server/IServer';
import { TReqConfigValidated } from '@/services/JSCService';
import { joinURL } from '@/utils';

export default class TransportationRequestBuilder extends SunnyRequestBuilder {
  static getBaseURIFromReq(req: IRequestWrap): URI {
    return req.config.Transportation;
  }

  createSearchStationsQuery(reqParams: ITransportationSearchStationsRequestParams): TReqConfigValidated {
    return this.validate(TransportationSearchStationsJSC, reqParams).map((params) =>
      this.makeConfig('GET', 'transportation/rail/search/stations', params),
    );
  }

  createSearchTrainsQuery(reqParams: ITransportationSearchTrainsRequestParams): TReqConfigValidated {
    return this.validate(TransportationSearchTrainsJSC, reqParams).map((params) =>
      this.makeConfig('GET', '/api/v1/trains', params, { timeout: 7500 }),
    );
  }

  createSearchTrainFaresQuery(reqParams: ITransportationSearchTrainFaresRequestParams): TReqConfigValidated {
    return this.validate(TransportationSearchTrainFaresJSC, reqParams).map((params) =>
      this.makeConfig('GET', '/transportation/rail/search/fares', params),
    );
  }

  createSearchTrainPackageFares(reqParams: ITransportationSearchTrainPackageFaresRequestParams): TReqConfigValidated {
    return this.validate(TransportationSearchTrainPackageFaresJSC, reqParams).map((params) => {
      const { trainNo } = params.resources;
      return this.makeConfig('GET', `/api/v1/trains/${trainNo}/fares`, params);
    });
  }

  createSearchTrainSeatMapCarriages(
    reqParams: ITransportationSearchTrainSeatMapCarriagesRequestParams,
  ): TReqConfigValidated {
    return this.validate(TransportationSearchTrainSeatMapCarriagesJSC, reqParams).map((params) => {
      const { trainNo } = params.resources;
      return this.makeConfig('GET', `/transportation/rail/search/seatMap/trains/${trainNo}/carriages`, params);
    });
  }

  createSearchTrainSeatMapCarriage(
    reqParams: ITransportationSearchTrainSeatMapCarriageRequestParams,
  ): TReqConfigValidated {
    return this.validate(TransportationSearchTrainSeatMapCarriageJSC, reqParams).map((params) => {
      const { trainNo, carriageNo } = params.resources;
      return this.makeConfig(
        'GET',
        `/transportation/rail/search/seatMap/trains/${trainNo}/carriages/${carriageNo}`,
        params,
      );
    });
  }

  createSearchTrainSchedule(reqParams: ITransportationSearchTrainScheduleRequestParams): TReqConfigValidated {
    return this.validate(TransportationSearchTrainScheduleJSC, reqParams).map((params) => {
      const { trainNo } = params.resources;
      return this.makeConfig('GET', `/transportation/rail/search/schedule/trains/${trainNo}`, params);
    });
  }

  protected getAPIEndpoint(path: string): string {
    const baseURI = this.baseURI.href();
    return baseURI ? joinURL(baseURI, path) : joinURL(baseURI, '/api/v1', path);
  }
}
